// Advisory services content.
export const ADVISORY_HERO = {
  title: "Independent Advice for Complex Business Decisions",
  intro:
    "MATRIVON provides focused advisory support where commercial opportunity, market knowledge and industrial realities need to be considered together.",
};

export const ADVISORY = [
  {
    slug: "formulation-support",
    icon: "Wrench",
    title: "PU & Silicone Formulation Support",
    short:
      "Expert polyurethane systems formulation and silicone compounding support for manufacturers who do not have an in-house formulation specialist.",
    lead:
      "Many manufacturers rely on polyurethane systems or silicone compounds every day, yet cannot justify hiring a full-time formulation expert. MATRIVON fills that gap: practical, independent formulation and compounding support so you can develop, optimise, troubleshoot — and, where it makes sense, compound in-house to control cost and quality.",
    topics: [
      "PU systems for discontinuous sandwich panels",
      "Cold-cure (HR) and flexible foam formulation",
      "Rigid foam for refrigeration, deep freezers and appliances",
      "Rigid PU insulation for solar water heaters and tanks",
      "Formulation troubleshooting and optimisation",
      "Silicone in-house compounding and cost reduction",
    ],
    approach: [
      { h: "Understand your process", p: "Your equipment, raw materials, applications and the specific problem you need to solve — panels, foam, insulation or silicone parts." },
      { h: "Work the formulation logic", p: "Reactive balance, raw-material selection, catalyst and additive strategy, and processing — building capability, not just handing over a recipe." },
      { h: "Enable independence", p: "Practical guidance so your team can formulate, troubleshoot or compound in-house with confidence and lower cost." },
    ],
    related: ["industrial-advisory", "strategic-projects"],
  },
  {
    slug: "market-entry-growth",
    icon: "Compass",
    title: "Market Entry & International Growth",
    short:
      "Assess market potential, competition, customers, route-to-market options and the practical realities of entering a new geography.",
    lead:
      "Entering a new country or region is rarely a matter of translating an existing strategy. Buying behaviour, distribution structures, competitors, pricing logic and regulatory context all change. MATRIVON helps you understand what actually matters before you commit resources.",
    topics: [
      "Market attractiveness and sizing",
      "Competitor and landscape analysis",
      "Route-to-market and channel options",
      "Partner and distributor strategy",
      "Country and region entry sequencing",
      "Go-to-market assessment and validation",
    ],
    approach: [
      { h: "Understand the market", p: "Real demand, segments, buying behaviour and the competitive structure, not just headline figures." },
      { h: "Test the commercial logic", p: "How value is created and captured, what pricing is realistic, and where margin actually sits." },
      { h: "Define a realistic path", p: "A route-to-market that fits your resources, risk appetite and time horizon." },
    ],
    related: ["distribution-strategy", "commercial-strategy"],
  },
  {
    slug: "commercial-strategy",
    icon: "TrendingUp",
    title: "Commercial Strategy",
    short:
      "Identify where growth can realistically come from, which opportunities deserve priority and how commercial resources can be deployed effectively.",
    lead:
      "Most businesses have more opportunities than resources. Commercial strategy is about choosing where to focus. MATRIVON helps leadership teams separate genuine growth from activity, and align commercial resources behind the decisions that matter.",
    topics: [
      "Growth opportunity identification",
      "Market and segment prioritisation",
      "Sales strategy and coverage",
      "Customer segmentation",
      "Value proposition definition",
      "Portfolio strategy",
    ],
    approach: [
      { h: "Find the real growth", p: "Where demand, capability and competitive position genuinely align." },
      { h: "Prioritise ruthlessly", p: "Rank opportunities by attractiveness and the ability to win, not internal enthusiasm." },
      { h: "Deploy resources", p: "Direct commercial effort, structure and investment toward the highest-return decisions." },
    ],
    related: ["market-entry-growth", "distribution-strategy"],
  },
  {
    slug: "distribution-strategy",
    icon: "Share2",
    title: "Distribution & Channel Strategy",
    short:
      "Evaluate distributors, channel structures and direct-sales alternatives with a focus on long-term market development rather than short-term volume.",
    lead:
      "The right channel decision shapes a market for years. MATRIVON evaluates distributors and channel structures on their ability to build the market you want, not just the volume they can move next quarter.",
    topics: [
      "Distributor selection and identification",
      "Distributor assessment and evaluation",
      "Distributor performance review",
      "Direct vs distributor strategy",
      "Channel structure design",
      "Partnership evaluation",
    ],
    approach: [
      { h: "Define the role of the channel", p: "What the market genuinely needs a partner to do, technically and commercially." },
      { h: "Evaluate beyond the pitch", p: "Capability, focus, alignment and market development potential, not just the customer list." },
      { h: "Structure for the long term", p: "Terms and incentives that reward market building, not only short-term volume." },
    ],
    related: ["market-entry-growth", "commercial-strategy"],
  },
  {
    slug: "industrial-advisory",
    icon: "FlaskConical",
    title: "Industrial & Chemical Advisory",
    short:
      "Combine technical understanding with commercial assessment across polymers, polyurethane, silicones, specialty chemicals and industrial materials.",
    lead:
      "In industrial markets, commercial decisions cannot be separated from technical reality. Product performance, formulation, application requirements and processing constraints all shape what is commercially possible. MATRIVON brings both perspectives to the same table.",
    topics: [
      "Polyurethane systems and applications",
      "Silicone elastomers and compounding",
      "Specialty chemicals and polymers",
      "Coatings, adhesives and sealants",
      "Industrial materials markets",
      "Technical-commercial assessment",
    ],
    approach: [
      { h: "Understand the technology", p: "What the material can and cannot do, and what that means for the application." },
      { h: "Read the market", p: "Who buys, why, and how technical requirements shape competition and pricing." },
      { h: "Assess what makes sense", p: "Where the technical-commercial case is genuinely strong, and where it is not." },
    ],
    related: ["strategic-projects", "commercial-strategy"],
  },
  {
    slug: "strategic-projects",
    icon: "Target",
    title: "Strategic Projects & Opportunity Assessment",
    short:
      "Independent support for feasibility studies, new business opportunities, market validation, commercial due diligence and selected investment assessments.",
    lead:
      "Some decisions are large enough to justify structured, independent analysis. MATRIVON supports feasibility studies, opportunity evaluation and commercial due diligence with a grounded view of both the market and the underlying industrial reality.",
    topics: [
      "Feasibility studies",
      "Opportunity evaluation",
      "Acquisition screening",
      "Commercial due diligence",
      "Technical-commercial reviews",
      "Investment assessment",
    ],
    approach: [
      { h: "Frame the question", p: "Define what the decision actually depends on before gathering data." },
      { h: "Analyse independently", p: "Test assumptions against market reality without an interest in the outcome." },
      { h: "Support the decision", p: "A clear view of opportunity and risk that leadership can act on." },
    ],
    related: ["industrial-advisory", "market-entry-growth"],
  },
  {
    slug: "executive-advisory",
    icon: "Handshake",
    title: "Executive / Retained Advisory",
    short:
      "Ongoing access to senior independent advice for executives, owners and commercial teams managing changing priorities.",
    lead:
      "Not every decision fits into a project. Retained advisory gives leadership teams ongoing access to a senior, independent sounding board across commercial, market and industrial questions as they arise.",
    topics: [
      "Ongoing strategic sounding board",
      "Commercial decision support",
      "Market and competitor monitoring",
      "Distributor and partner questions",
      "Opportunity screening",
      "Leadership-level challenge and review",
    ],
    approach: [
      { h: "Continuity", p: "A trusted perspective that understands your business and its context over time." },
      { h: "Availability", p: "Senior input when decisions arise, without restarting the context each time." },
      { h: "Independence", p: "Honest challenge, free of internal politics or vendor interest." },
    ],
    related: ["commercial-strategy", "strategic-projects"],
  },
];

export function getAdvisory(slug) {
  return ADVISORY.find((a) => a.slug === slug);
}
